import React, { useMemo, useRef, useState } from 'react';
import { DrawerActions, useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { Box, Container, Divider, IconButton, IContainerProps, Image, ScrollView, Tooltip, useTheme, View, VStack } from 'native-base';
import { Asset } from 'expo-asset';
import { find, findLast, groupBy, map, merge } from 'lodash';
import { useQueryClient } from 'react-query';

import { ChevronRightDoubleIcon, Modal, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { ModuleScreen, useAppModules } from '../context';
import { useAuthPermissions } from '../context';
import { PinAccessModal } from '@app/features/store-core/component/pin-access';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { SelectEmployee } from '@app/features/store-core/component/select-employee';
import { EmployeeDto } from '@pimm/services/lib/sms-workforce';
import { PinSetupModalType } from '@app/features/store-core/types';
import { UserBySiteKeyDto } from '@pimm/services/lib/sms-tenants';
import { FormUserPasskey } from '@app/features/store-core/component/form-user-passkey';

export type TabContainerProps = IContainerProps & {
  element?: React.ElementType;
  onPressMenuItem?: (screen: ModuleScreen) => void;
};

export const TabContainer: React.FC<TabContainerProps> = ({ children, element, onPressMenuItem, ...props }) => {
  const { colors } = useTheme();
  // const navigation = useNavigation<DrawerNavigationProp<MainNavigationParams>>();
  // TODO: Fix useNavigation typing issue and passing of default/history params
  const navigation = useNavigation();
  const route = useRoute();
  const navigationState = useNavigationState(state => state);
  const { translate } = useAppLocale();

  const { screens } = useAppModules();

  const history = (navigationState?.history ?? []) as { key: string; type: string }[];
  const lastRoute = findLast(history, _ => _.type === 'route');
  const params = route.params as { siteId?: string };
  const permissions = useAuthPermissions(state => state.permissions);
  const queryClient = useQueryClient();
  const pinModal = useModalFocus();
  const employeeModal = useModalFocus();
  const pinFormModal = useModalFocus();
  const pendingScreenRef = useRef<ModuleScreen | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<UserBySiteKeyDto | null>(null);

  const menuItems = useMemo(() => {
    return map(groupBy(screens, 'payload.sectionTitle'), (screens, category) => ({
      title: category,
      name: category,
      children: screens,
    }));
  }, [screens]);

  const handlePressMenu = () => {
    navigation.dispatch(DrawerActions.openDrawer());
  };

  const handlePressItem = (screen: ModuleScreen) => () => {
    const isPinRestricted = !!permissions?.[`appModule${screen.name}PinRestricted`];
    if (isPinRestricted) {
      pendingScreenRef.current = screen;
      pinModal.setOpen();
      return;
    }
    if (screen.component) {
      // @ts-ignore
      navigation.navigate('Main', { screen: screen.name, siteId: params.siteId });
    }
    if (onPressMenuItem) onPressMenuItem(screen);
  };

  const handlePinUnlock = () => {
    openEmployeeModal();
  };
  const openEmployeeModal = () => {
    pinModal.setHide();
    employeeModal.setOpen();
  };
  const handleSelectEmployee = (employee: UserBySiteKeyDto) => {
    setSelectedEmployee(employee);
    employeeModal.setHide();
    pinFormModal.setOpen();
  };

  const handlePinSuccess = () => {
    pinFormModal.setHide();
    if (!pendingScreenRef.current) return;
    // @ts-ignore
    navigation.navigate('Main', { screen: pendingScreenRef.current.name, siteId: params.siteId });
    pendingScreenRef.current = null;
  };

  const handleChangePin = ({ id: _userId }: Partial<UserBySiteKeyDto>) => {
    const queryCache = queryClient.getQueryCache();
    const queryGetUsersWithSiteKey = find(queryCache.getAll(), _ => _.queryKey[0] === 'GetUsersWithSiteKey');

    if (queryGetUsersWithSiteKey) {
      const queryKey = ['GetUsersWithSiteKey', queryGetUsersWithSiteKey?.queryKey[1]];
      queryClient.setQueryData<UserBySiteKeyDto[]>(queryKey, (_prevData): UserBySiteKeyDto[] =>
        map(_prevData, _user => (_user.id === _userId ? { ..._user, pinIsSet: true } : _user)),
      );
    }
  };

  return (
    <Container size="full" maxW="full" safeAreaTop {...props}>
      <View flexDirection="row" size="full" overflowY="hidden">
        <View position="sticky" top={0} left={0} pt={1} h="full" w={{ md: 60, xl: 70 }} zIndex={1} bg="gray.900" overflowY="auto">
          <ScrollView
            h="full"
            contentContainerStyle={{ flexGrow: 1 }}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
          >
            <Box alignItems="center" justifyContent="center" mb={1} h={12}>
              <IconButton
                accessibilityHint="This will open the drawer menu"
                rounded="full"
                p={0}
                size={8}
                bg="white"
                _hover={{ bg: 'white' }}
                _pressed={{ bg: 'gray.100' }}
                icon={<ChevronRightDoubleIcon size={3} color="black" />}
                onPress={handlePressMenu}
              />
            </Box>

            <VStack flex={1} pb={2}>
              {menuItems.map((item, index) => {
                return (
                  <VStack key={item.name} space={0.5} w="full" alignItems="center">
                    {!!index && <Divider mt={1} mb={0.5} w={{ md: 8, xl: 10 }} backgroundColor="gray.600" />}
                    {item.children?.map(screen => {
                      const isActive = !!lastRoute && lastRoute.key.split('-')[0] === screen.name;
                      const title = translate(screen.title, screen.titleTranslations);
                      return (
                        <Tooltip key={screen.name} label={title ?? ''} openDelay={500} placement="right" bgColor="gray.700">
                          <IconButton
                            key={item.name}
                            icon={React.createElement(
                              screen.icon,
                              merge({ size: 'md', color: colors.gray[isActive ? 100 : 300] }, screen.iconProps || {}),
                            )}
                            _hover={{ bg: isActive ? 'gray.700' : 'gray.900' }}
                            bg={isActive ? 'gray.700' : undefined}
                            color={screen.disabled ? 'gray.500' : 'gray.50'}
                            rounded="md"
                            isDisabled={screen.disabled}
                            h={12}
                            w={12}
                            onPress={handlePressItem(screen)}
                          />
                        </Tooltip>
                      );
                    })}
                  </VStack>
                );
              })}
            </VStack>

            <Box position="sticky" bottom={0} alignItems="center" justifyContent="center" h={12} bg="gray.800">
              <Box rounded="full" size="32px" alignItems="center" justifyContent="center" bg="white">
                <Image
                  source={{
                    uri: Asset.fromModule(require('assets/opsbee.svg')).uri,
                  }}
                  resizeMode="contain"
                  resizeMethod="scale"
                  size="30px"
                  justifyContent="center"
                />
              </Box>
            </Box>
          </ScrollView>
        </View>
        <View flex={1} zIndex={0}>
          {element ? React.createElement(element, {}) : children}
        </View>
      </View>
      <PinAccessModal
        isOpen={pinModal.isOpen}
        onClose={() => {
          pinModal.setHide();
        }}
        onUnlock={handlePinUnlock}
      />
      <Modal
        size="md"
        noPadding
        hideClose
        _content={{ rounded: 'xl', maxHeight: '90%' }}
        isOpen={employeeModal.isOpen}
        onClose={employeeModal.setHide}
      >
        <SelectEmployee isManager={false} onSelectEmployee={handleSelectEmployee} onClose={employeeModal.setHide} />
      </Modal>

      <Modal size="md" _content={{ rounded: 'xl', p: 6, maxWidth: 320 }} isOpen={pinFormModal.isOpen} noPadding hideClose>
        <FormUserPasskey
          siteId={params?.siteId}
          user={selectedEmployee ?? undefined}
          onChange={handleChangePin}
          onClose={pinFormModal.setHide}
          onSuccess={async () => {
            pinFormModal.setHide();
            handlePinSuccess();
          }}
        />
      </Modal>
    </Container>
  );
};
